import PropTypes from 'prop-types'
import { createContext, useContext, useEffect, useState } from 'react'
import { NumberParam, useQueryParam } from 'use-query-params'

import useUserAccess from 'Common/hooks/useUserAccess'
import getRandomFlashcards from '../flashcards/services/getRandomFlashcards'
import getFlashcard from '../flashcards/services/getFlashcard'
import getRandomVideos from '../videos/services/getRandomVideos'

// Context objects
const MediaGalleryState = createContext()
const MediaGalleryDispatch = createContext()

MediaGalleryState.displayName = 'MediaGalleryState'
MediaGalleryDispatch.displayName = 'MediaGalleryDispatch'

// Context hook
function useMediaGallery() {
  const state = useContext(MediaGalleryState)
  const dispatch = useContext(MediaGalleryDispatch)

  if (state === undefined || dispatch === undefined) {
    throw new Error(
      'useMediaGallery must be used within a MediaGalleryProvider'
    )
  }

  return [state, dispatch]
}

function MediaGalleryProvider({ children }) {
  const userAccess = useUserAccess()
  const [fetchedFlashcards, setFetchedFlashcards] = useState([])
  const [fetchedVideos, setFetchedVideos] = useState([])
  const [fetchedIds, setFetchedIds] = useState([])
  const [totalPages, setTotalPages] = useState(0)
  const [page, setPage] = useState(0)

  const [flashcardId] = useQueryParam('flashcard', NumberParam)

  const fetchRandomFlashcards = async () => {
    const randomFlashcards = await getRandomFlashcards({
      exclude: fetchedIds,
    })

    const flashcards = fetchedFlashcards.concat(randomFlashcards)
    const flashcardIDs = fetchedIds.concat(randomFlashcards.map(fc => fc.id))
    if (
      flashcardId &&
      fetchedFlashcards.length === 0 &&
      !flashcardIDs.includes(flashcardId)
    ) {
      const flashcard = await getFlashcard(flashcardId)

      setFetchedFlashcards(flashcards.concat(flashcard))
      return setFetchedIds(flashcardIDs.concat(flashcard.id))
    }
    setFetchedFlashcards(flashcards)
    setFetchedIds(flashcardIDs)
  }

  const fetchRandomVideos = async () => {
    const { data: randomVideos, headers } = await getRandomVideos({
      page: page + 1,
    })
    setFetchedVideos(current => current.concat(randomVideos))
    setPage(Number(headers['x-page']))
    setTotalPages(Number(headers['x-total-pages']))
  }

  const fetchMedia = type => {
    if (type === 'flashcards') {
      return fetchRandomFlashcards()
    }
    return fetchRandomVideos()
  }

  useEffect(() => {
    // Initial fetch on component mount
    !fetchedFlashcards.length && fetchRandomFlashcards()
    !fetchedVideos.length && fetchRandomVideos()
  }, [])

  const state = {
    randomFlashcards: fetchedFlashcards,
    randomVideos: {
      data: fetchedVideos,
      currentPage: page,
      totalPages,
    },
    userAccess,
  }

  return (
    <MediaGalleryState.Provider value={state}>
      <MediaGalleryDispatch.Provider value={fetchMedia}>
        {children}
      </MediaGalleryDispatch.Provider>
    </MediaGalleryState.Provider>
  )
}

MediaGalleryProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export { MediaGalleryProvider, useMediaGallery }
